import {
    ApplicationRef,
    Injectable,
    Injector,
    ComponentFactory,
    ComponentFactoryResolver,
    ComponentRef,
    Type,
} from '@angular/core';

import { ModalDialogOverlayComponent } from './modal-dialog-overlay.component';
import { ModalDialogActive, ModalDialogRef } from './modal-dialog-ref.service';
import { ModalDialogContentRef } from './modal-dialog-utils';
import { ModalDialogWindowComponent } from './modal-dialog-window.component';
import { ModalDialogOptions } from './modal-dialog.service';

@Injectable()
export class ModalDialogStack {
    private readonly overlayFactory: ComponentFactory<ModalDialogOverlayComponent>;
    private readonly windowFactory: ComponentFactory<ModalDialogWindowComponent>;

    constructor(
        private readonly applicationRef: ApplicationRef,
        private readonly injector: Injector,
        private readonly componentFactoryResolver: ComponentFactoryResolver,
    ) {
        this.overlayFactory = this.componentFactoryResolver.resolveComponentFactory(ModalDialogOverlayComponent);
        this.windowFactory = this.componentFactoryResolver.resolveComponentFactory(ModalDialogWindowComponent);
    }

    open<ClosedWithType, DialogType>(
        moduleCFR: ComponentFactoryResolver,
        contentInjector: Injector,
        dialogType: Type<DialogType>,
        options: ModalDialogOptions<DialogType>,
    ): ModalDialogRef<ClosedWithType, DialogType> {
        const containerSelector = options?.container || 'body';
        const containerEl = document.querySelector(containerSelector);

        if (!containerEl) {
            throw new Error(`The specified modal container "${containerSelector}" was not found in the DOM.`);
        }

        const activeDialog = new ModalDialogActive<ClosedWithType>();
        const contentRef = this.getContentRef(
            moduleCFR,
            options.injector ?? contentInjector,
            dialogType,
            activeDialog);

        if (options.data && contentRef.componentRef) {
            for (const name in options.data) {
                if (Object.prototype.hasOwnProperty.call(options.data, name)) {
                    contentRef.componentRef.instance[name as keyof DialogType] = options.data[name];
                }
            }
        }

        let overlayCompRef: ComponentRef<ModalDialogOverlayComponent> | undefined;

        if (options.backdrop !== false) {
            overlayCompRef = this.overlayFactory.create(this.injector);
            this.applicationRef.attachView(overlayCompRef.hostView);
            containerEl.appendChild(overlayCompRef.location.nativeElement);
        }

        const windowCompRef = this.windowFactory.create(this.injector, contentRef.nodes);
        this.applicationRef.attachView(windowCompRef.hostView);
        containerEl.appendChild(windowCompRef.location.nativeElement);

        const modalDialogRef = new ModalDialogRef<ClosedWithType, DialogType>(windowCompRef, contentRef, overlayCompRef);

        activeDialog.close = (result) => modalDialogRef.close(result);
        activeDialog.dismiss = (reason) => modalDialogRef.dismiss(reason);

        this.applyModalWindowOptions(windowCompRef.instance, options);

        return modalDialogRef;
    }

    private applyModalWindowOptions<DialogType>(windowInstance: ModalDialogWindowComponent, options: ModalDialogOptions<DialogType>): void {
        if (options.backdrop != null) {
            windowInstance.backdrop = options.backdrop;
        }

        if (options.keyboard != null) {
            windowInstance.keyboard = options.keyboard;
        }

        if (options.size != null) {
            windowInstance.size = options.size;
        }

        if (options.windowClass != null) {
            windowInstance.windowClass = options.windowClass;
        }
    }

    private getContentRef<ClosedWithType, DialogType>(
        moduleCFR: ComponentFactoryResolver,
        contentInjector: Injector,
        dialogType: Type<DialogType>,
        context: ModalDialogActive<ClosedWithType>,
    ): ModalDialogContentRef<DialogType> {
        const contentCompFactory = moduleCFR.resolveComponentFactory(dialogType);
        const modalContentInjector = Injector.create({
            providers: [
                { provide: ModalDialogActive, useValue: context },
            ],
            parent: contentInjector,
        });
        const componentRef = contentCompFactory.create(modalContentInjector);
        this.applicationRef.attachView(componentRef.hostView);
        return new ModalDialogContentRef([[componentRef.location.nativeElement as HTMLElement]], componentRef.hostView, componentRef);
    }
}
